import { SelfAccountKeyring } from '../keyring/account_self';
import { AbstractSelfAccountKeyring } from '../keyring/account_base';
import { AbstractPrivateKeyring } from '../keyring/private';
import { account_password_change } from './password';
import { ACCOUNT_ROOT_KEY_CRYPTO_CONTEXT } from '../context/account_key/root/__init__';
import { AccountRootKeyConfigLoadArgs } from '../context/account_key/root/args';
import { Ed25519KeyGenerator } from '../base/key/ec';
import { ACCOUNT_SIGNATURE_CRYPTO_CONTEXT } from '../context/account_signature/__init__';
import { AccountSignatureConfigDumpArgs } from '../context/account_signature/args';
import { base64 } from 'rfc4648';
import { makeInfoByteArray } from '../utility';
import { MLDSA87KeyGenerator } from '../base/key/ml';
import { CU_PQC_ML } from '../context/feature';

export interface create_account_recovery_confirm_request_type {
  recovery_token: string;
  salt: string;
  master_key: string;
  auth_key: string;
  signature: string;
}

/**
 *
 * @param private_kr_cls
 * @param recovery_token acquired from URL fragment, sent to user as email by recover-prepare
 * @param account_id returned by recover-challenge for use here
 * @param encrypted_signing_secret_ec returned by recover-challenge for use here
 * @param recovery_key provided by the user
 * @param new_password provided by the user
 * @param enforce_normalized_password for testing
 * @param encrypted_signing_secret_keys
 * @param signing_public_keys
 */
export async function create_account_recovery_confirm_request(
  private_kr_cls: typeof AbstractPrivateKeyring,
  recovery_token: string,
  account_id: string,
  encrypted_signing_secret_ec: Uint8Array | null, // for legacy api compatibility
  recovery_key: Uint8Array,
  new_password: string,
  enforce_normalized_password?: boolean,
  encrypted_signing_secret_keys?: Object, // prefer using this to encrypted_signing_secret_ec
  signing_public_keys?: Object // MLDSA needs the public key for regenerate
): Promise<create_account_recovery_confirm_request_type> {
  let kr_variant = SelfAccountKeyring.VARIANT_BASE;
  let required_cu = null;
  let recovery_kr = new SelfAccountKeyring(
    account_id,
    {},
    { [AbstractSelfAccountKeyring.MASTER]: recovery_key }
  );
  let [client_salt_input, encrypted_master_key, hashed_authentication_key] =
    await account_password_change(
      private_kr_cls,
      recovery_kr,
      new_password,
      enforce_normalized_password
    );
  let encrypted_signing_secret_ml = null, signing_public_ml = null;
  if (encrypted_signing_secret_keys) {
    encrypted_signing_secret_ec = encrypted_signing_secret_keys[SelfAccountKeyring.ED25519];
    encrypted_signing_secret_ml = encrypted_signing_secret_keys[SelfAccountKeyring.MLDSA87];
    if (signing_public_keys) {
      signing_public_ml = signing_public_keys[SelfAccountKeyring.MLDSA87];
    }
    if (encrypted_signing_secret_ml || signing_public_ml) {
      if (!(encrypted_signing_secret_ml && signing_public_ml)) {
        throw new Error('ML-DSA keys need to be all present');
      }
      kr_variant = SelfAccountKeyring.VARIANT_ML;
      required_cu = CU_PQC_ML;
    }
  }

  let ed25519_key = Ed25519KeyGenerator.regenerate_key_pair(
    await ACCOUNT_ROOT_KEY_CRYPTO_CONTEXT.load(
      new AccountRootKeyConfigLoadArgs(
        encrypted_signing_secret_ec,
        recovery_kr,
        SelfAccountKeyring.ED25519,
        kr_variant
      ),
      required_cu
    )
  );

  recovery_kr.update(
    { [SelfAccountKeyring.ED25519]: ed25519_key.publicKey },
    { [SelfAccountKeyring.ED25519]: ed25519_key.secretKey }
  );

  if (kr_variant === SelfAccountKeyring.VARIANT_ML) {
    let mldsaKeyPair = MLDSA87KeyGenerator.regenerate_key_pair(
      await ACCOUNT_ROOT_KEY_CRYPTO_CONTEXT.load(
        new AccountRootKeyConfigLoadArgs(
          encrypted_signing_secret_ml,
          recovery_kr,
          SelfAccountKeyring.MLDSA87,
          kr_variant
        ),
        required_cu
      ),
      signing_public_ml
    );
    recovery_kr.update(
      { [SelfAccountKeyring.MLDSA87]: mldsaKeyPair.publicKey },
      { [SelfAccountKeyring.MLDSA87]: mldsaKeyPair.secretKey }
    );
  }

  let signature = await ACCOUNT_SIGNATURE_CRYPTO_CONTEXT.dump(
    new AccountSignatureConfigDumpArgs(
      makeInfoByteArray([
        recovery_token,
        client_salt_input,
        encrypted_master_key,
        hashed_authentication_key,
      ]),
      recovery_kr
    ),
    required_cu
  );
  return {
    recovery_token: recovery_token,
    salt: base64.stringify(client_salt_input),
    master_key: base64.stringify(encrypted_master_key),
    auth_key: base64.stringify(hashed_authentication_key),
    signature: base64.stringify(signature),
  };
}
